import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit, Loader2, Tag, Percent, Euro, ToggleLeft, ToggleRight } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const EMPTY_FORM = {
  code: '', type: 'percent', value: 10, min_order: 0, max_uses: '', expires_at: '', active: true, description: '',
};

export default function AdminDiscountCodesPage() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/discount-codes');
      setCodes(r.data);
    } catch (e) {
      toast.error('Errore caricamento codici');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code,
      type: c.type,
      value: c.value,
      min_order: c.min_order || 0,
      max_uses: c.max_uses ?? '',
      expires_at: c.expires_at ? c.expires_at.slice(0, 16) : '',
      active: c.active,
      description: c.description || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: Number(form.value),
        min_order: Number(form.min_order || 0),
        max_uses: form.max_uses === '' ? null : Number(form.max_uses),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: form.active,
        description: form.description || '',
      };
      if (editing) {
        await api.patch(`/admin/discount-codes/${editing.id}`, payload);
        toast.success('Codice aggiornato');
      } else {
        await api.post('/admin/discount-codes', payload);
        toast.success('Codice creato');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/discount-codes/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error('Errore'); }
  };

  const del = async (c) => {
    if (!window.confirm(`Eliminare il codice ${c.code}?`)) return;
    try {
      await api.delete(`/admin/discount-codes/${c.id}`);
      toast.success('Codice eliminato');
      load();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <AdminLayout testid="admin-discount-codes-content">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Codici sconto</h1>
          <p className="text-sm text-[--brand-muted] mt-1">Crea codici promozionali per i tuoi clienti</p>
        </div>
        <Button onClick={openCreate} className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="admin-discount-add-button">
          <Plus size={16} className="mr-1"/>Nuovo codice
        </Button>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-white border border-[--brand-border] p-8 text-center text-[--brand-muted]"><Loader2 className="animate-spin inline mr-2" size={16}/> Caricamento...</div>
      ) : codes.length === 0 ? (
        <div className="rounded-2xl bg-white border border-[--brand-border] p-8 text-center">
          <Tag size={32} className="mx-auto text-[--brand-muted] mb-2"/>
          <div className="font-semibold">Nessun codice sconto</div>
          <div className="text-sm text-[--brand-muted] mt-1">Crea il tuo primo codice promozionale.</div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {codes.map(c => (
            <div key={c.id} className="rounded-2xl bg-white border border-[--brand-border] p-4 shadow-[var(--shadow-sm)]" data-testid="admin-discount-card">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-lg font-bold tracking-wide">{c.code}</span>
                    {c.active ? (
                      <Badge className="bg-[--brand-primary]/10 text-[--brand-primary] border-[--brand-primary]/30">Attivo</Badge>
                    ) : (
                      <Badge variant="secondary">Disattivo</Badge>
                    )}
                  </div>
                  {c.description && <div className="text-xs text-[--brand-muted] mt-1">{c.description}</div>}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => toggleActive(c)} title={c.active ? 'Disattiva' : 'Attiva'}>
                    {c.active ? <ToggleRight size={16}/> : <ToggleLeft size={16}/>}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(c)} title="Modifica">
                    <Edit size={14}/>
                  </Button>
                  <Button variant="ghost" size="icon" className="text-[--brand-danger]" onClick={() => del(c)} title="Elimina">
                    <Trash2 size={14}/>
                  </Button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                {c.type === 'percent' ? <Percent size={14} className="text-[--brand-primary]"/> : <Euro size={14} className="text-[--brand-primary]"/>}
                <span className="font-display text-2xl font-bold text-[--brand-primary]">
                  {c.type === 'percent' ? `${c.value}%` : `€${c.value.toFixed(2)}`}
                </span>
              </div>
              <div className="mt-3 grid gap-1 text-xs text-[--brand-muted]">
                {c.min_order > 0 && <div>Min ordine: €{c.min_order.toFixed(2)}</div>}
                <div>Utilizzi: {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ' / ∞'}</div>
                {c.expires_at && <div>Scade: {new Date(c.expires_at).toLocaleString('it-IT')}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica codice' : 'Nuovo codice sconto'}</DialogTitle>
            <DialogDescription>Configura le regole del codice promozionale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Codice</Label>
              <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="BENVENUTO10" className="rounded-xl uppercase" data-testid="discount-form-code" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Tipo sconto</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="discount-form-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentuale (%)</SelectItem>
                    <SelectItem value="fixed">Importo fisso (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valore {form.type === 'percent' ? '(0-100)' : '(€)'}</Label>
                <Input type="number" step="0.01" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} className="rounded-xl" data-testid="discount-form-value" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Ordine minimo (€)</Label>
                <Input type="number" step="0.01" value={form.min_order} onChange={e => setForm(f => ({ ...f, min_order: e.target.value }))} className="rounded-xl" data-testid="discount-form-min-order" />
              </div>
              <div>
                <Label>Utilizzi massimi (vuoto = illimitato)</Label>
                <Input type="number" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} className="rounded-xl" placeholder="∞" data-testid="discount-form-max-uses" />
              </div>
            </div>
            <div>
              <Label>Data scadenza (opzionale)</Label>
              <Input type="datetime-local" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} className="rounded-xl" data-testid="discount-form-expires" />
            </div>
            <div>
              <Label>Descrizione (opzionale)</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="rounded-xl" placeholder="Es. Sconto di benvenuto per nuovi clienti" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="accent-[--brand-primary]" />
              Codice attivo
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving || !form.code.trim()} className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="discount-form-save">
              {saving ? <Loader2 size={14} className="animate-spin mr-2"/> : null}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
